import {
  CircleDollarSign,
  Download,
  Eye,
  LayoutGrid,
  Layers,
  List as ListIcon,
  Plus,
  Search,
  Table as TableIcon,
  Trash2,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PdfPreviewDialog } from '@/components/PdfPreviewDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangeFilter } from '@/components/ui/date-range-filter';
import { EntitySelect } from '@/components/EntitySelect';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatMoney } from '@/lib/money';
import {
  BASE as STAFF_INVOICES_BASE,
  downloadStaffInvoice,
  getStaffInvoiceBlob,
  markStaffInvoicePaid,
  staffInvoiceFileName,
} from '../api';
import {
  STAFF_INVOICE_STATUSES,
  staffInvoiceStatusLabel,
  staffInvoiceStatusVariant,
} from '../constants';
import { useDeleteStaffInvoice, useStaffInvoiceAction, useStaffInvoices } from '../queries';
import { resolveStaffInvoiceEmployeeLabel, searchStaffInvoiceEmployees } from '../select';
import { useStaffInvoicesUi, type StaffInvoicesViewMode } from '../store/viewPrefs';
import type { StaffInvoice } from '../types';

type StaffInvoiceOrderingColumn = 'number' | 'status' | 'period_start' | 'base_amount' | 'issue_date';

const VIEW_MODES: readonly ViewModeOption<StaffInvoicesViewMode>[] = [
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');
const money = (inv: StaffInvoice) => formatMoney(inv.total, inv.currency) ?? '—';

export function StaffInvoicesListPage() {
  const navigate = useNavigate();
  const view = useStaffInvoicesUi((s) => s.view);
  const setView = useStaffInvoicesUi((s) => s.setView);
  const ordering = useStaffInvoicesUi((s) => s.ordering);
  const setOrdering = useStaffInvoicesUi((s) => s.setOrdering);

  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employee, setEmployee] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);

  const { data, isPending, isError, error, isFetching } = useStaffInvoices({
    page,
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    employee: employee ? Number(employee) : undefined,
    after: from || undefined,
    before: to || undefined,
    ordering,
  });

  const results = data?.results ?? [];
  const open = (inv: StaffInvoice) => navigate(`/finance/staff-invoices/${inv.public_id}`);

  // Inline PDF preview — fetched as an auth blob (raw media `src` 401s / fails CORS).
  const [preview, setPreview] = useState<StaffInvoice | null>(null);
  const previewPublicId = preview?.public_id ?? null;
  const fetchPreviewPdf = useCallback(
    () => getStaffInvoiceBlob(previewPublicId as string),
    [previewPublicId],
  );

  const markPaid = useStaffInvoiceAction(markStaffInvoicePaid, 'Invoice marked as paid');
  const del = useDeleteStaffInvoice();
  const rowActions: RowActions = {
    onView: setPreview,
    onDownload: (inv) => downloadStaffInvoice(inv.public_id, staffInvoiceFileName(inv)),
    onMarkPaid: (inv) => markPaid.mutate(inv.public_id),
    markPaidPending: markPaid.isPending,
    onDelete: (inv) => del.mutate(inv.id),
    deletePending: del.isPending,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Staff invoices</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Invoices staff raise to get paid — salary, commissions and reimbursements. Client billing
            lives under Billing.
          </p>
        </div>
        <Button onClick={() => navigate('/finance/staff-invoices/new')}>
          <Plus /> New invoice
        </Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setPage(1);
                setSearchInput(e.target.value);
              }}
              placeholder="Search by number or employee…"
              className="pl-8"
              aria-label="Search staff invoices"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setPage(1);
              setStatusFilter(v);
            }}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STAFF_INVOICE_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="w-56">
            <EntitySelect
              queryKey="staff-invoice-employee-filter"
              value={employee}
              onChange={(v) => {
                setPage(1);
                setEmployee(v);
              }}
              search={searchStaffInvoiceEmployees}
              resolveLabel={resolveStaffInvoiceEmployeeLabel}
              placeholder="All employees"
              clearable
            />
          </div>
          <DateRangeFilter
            label="Issued"
            idPrefix="staff-invoice-issued"
            from={from}
            to={to}
            onFromChange={(v) => {
              setPage(1);
              setFrom(v);
            }}
            onToChange={(v) => {
              setPage(1);
              setTo(v);
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              void exportCsv(STAFF_INVOICES_BASE, {
                search: search || undefined,
                status: statusFilter === 'all' ? undefined : statusFilter,
                employee: employee || undefined,
                after: from || undefined,
                before: to || undefined,
                ordering,
                export: 'csv',
              });
            }}
            aria-label="Export staff invoices as CSV"
            title="Export staff invoices as CSV"
          >
            <Download className="size-4" />
          </Button>
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} />
        </div>
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <StaffInvoicesSkeleton />
      ) : results.length === 0 ? (
        <StaffInvoicesTable
          rows={[]}
          onRowClick={() => undefined}
          ordering={ordering}
          onOrderingChange={setOrdering}
          actions={rowActions}
        />
      ) : view === 'table' ? (
        <StaffInvoicesTable
          rows={results}
          onRowClick={open}
          ordering={ordering}
          onOrderingChange={setOrdering}
          actions={rowActions}
        />
      ) : view === 'list' ? (
        <StaffInvoicesList rows={results} onOpen={open} />
      ) : view === 'cards' ? (
        <StaffInvoicesCards rows={results} onOpen={open} />
      ) : (
        <StaffInvoicesGroupedByStatus rows={results} onOpen={open} />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'invoice' : 'invoices'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <PdfPreviewDialog
        open={Boolean(preview)}
        onOpenChange={(o) => !o && setPreview(null)}
        title={`${preview?.number || 'Staff invoice'} — PDF`}
        fetchBlob={previewPublicId ? fetchPreviewPdf : undefined}
      />
    </div>
  );
}

// ---- View renderers --------------------------------------------------------

interface ListProps {
  rows: StaffInvoice[];
  onOpen: (inv: StaffInvoice) => void;
}

interface RowActions {
  onView: (inv: StaffInvoice) => void;
  onDownload: (inv: StaffInvoice) => void;
  onMarkPaid: (inv: StaffInvoice) => void;
  markPaidPending: boolean;
  onDelete: (inv: StaffInvoice) => void;
  deletePending: boolean;
}

interface TableProps {
  rows: StaffInvoice[];
  onRowClick: (inv: StaffInvoice) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
  actions: RowActions;
}

/** Row actions cell — download the PDF (when generated) and mark a signed/issued
 * invoice as paid. Clicks stop propagation so the row's navigate doesn't fire. */
function StaffInvoiceRowActions({ inv, actions }: { inv: StaffInvoice; actions: RowActions }) {
  // Mark-paid only once signed (mirrors the detail page) — an unsigned invoice
  // isn't payable yet.
  const canMarkPaid = inv.status === 'signed';
  // A signed/paid invoice is a committed record — can't be deleted (backend-guarded).
  const canDelete = inv.status !== 'signed' && inv.status !== 'paid';
  // stopPropagation per-button so the row's navigate doesn't fire on action click.
  return (
    <div className="flex items-center justify-end gap-1">
      {inv.document ? (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="View PDF"
            title="View PDF"
            onClick={(e) => {
              e.stopPropagation();
              actions.onView(inv);
            }}
          >
            <Eye className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label="Download PDF"
            title="Download PDF"
            onClick={(e) => {
              e.stopPropagation();
              actions.onDownload(inv);
            }}
          >
            <Download className="size-4" />
          </Button>
        </>
      ) : null}
      {canMarkPaid ? (
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label="Mark as paid"
          title="Mark as paid"
          disabled={actions.markPaidPending}
          onClick={(e) => {
            e.stopPropagation();
            actions.onMarkPaid(inv);
          }}
        >
          <CircleDollarSign className="size-4" />
        </Button>
      ) : null}
      {canDelete ? (
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label="Delete invoice"
              title="Delete invoice"
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 className="size-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent onClick={(e) => e.stopPropagation()}>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this invoice?</AlertDialogTitle>
              <AlertDialogDescription>
                {inv.number ? `Invoice ${inv.number}` : 'This invoice'} will be permanently
                deleted. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={actions.deletePending}
                onClick={(e) => {
                  e.stopPropagation();
                  actions.onDelete(inv);
                }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}

function StaffInvoicesTable({ rows, onRowClick, ordering, onOrderingChange, actions }: TableProps) {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <SortableTableHead<StaffInvoiceOrderingColumn>
              column="number"
              value={ordering}
              onChange={onOrderingChange}
            >
              Number
            </SortableTableHead>
            <TableHead>Employee</TableHead>
            <SortableTableHead<StaffInvoiceOrderingColumn>
              column="period_start"
              value={ordering}
              onChange={onOrderingChange}
              className="hidden md:table-cell"
            >
              Period
            </SortableTableHead>
            <SortableTableHead<StaffInvoiceOrderingColumn>
              column="base_amount"
              value={ordering}
              onChange={onOrderingChange}
              className="text-right"
            >
              Total
            </SortableTableHead>
            <SortableTableHead<StaffInvoiceOrderingColumn>
              column="status"
              value={ordering}
              onChange={onOrderingChange}
            >
              Status
            </SortableTableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-muted-foreground py-10 text-center">
                No staff invoices yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((inv) => (
              <TableRow key={inv.id} className="cursor-pointer" onClick={() => onRowClick(inv)}>
                <TableCell className="font-medium">{inv.number || '—'}</TableCell>
                <TableCell className="text-muted-foreground">{inv.employee_name}</TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{money(inv)}</TableCell>
                <TableCell>
                  <Badge variant={staffInvoiceStatusVariant(inv.status)}>
                    {staffInvoiceStatusLabel(inv.status)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <StaffInvoiceRowActions inv={inv} actions={actions} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function StaffInvoicesCards({ rows, onOpen }: ListProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((inv) => (
        <Card
          key={inv.id}
          role="button"
          tabIndex={0}
          onClick={() => onOpen(inv)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onOpen(inv);
            }
          }}
          className="hover:bg-accent/30 cursor-pointer transition-colors"
        >
          <CardHeader>
            <CardTitle className="text-base">{inv.number || inv.employee_name}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-center gap-2 text-xs">
            <Badge variant={staffInvoiceStatusVariant(inv.status)}>
              {staffInvoiceStatusLabel(inv.status)}
            </Badge>
            <span className="text-muted-foreground truncate">{inv.employee_name}</span>
            <span className="text-foreground ml-auto tabular-nums">{money(inv)}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function StaffInvoicesList({ rows, onOpen }: ListProps) {
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((inv) => (
        <li key={inv.id}>
          <button
            type="button"
            onClick={() => onOpen(inv)}
            className="hover:bg-accent/30 flex w-full cursor-pointer items-center gap-3 px-3 py-2 text-left"
          >
            <span className="min-w-0 flex-1 truncate text-sm font-semibold">
              {inv.number || inv.employee_name}
            </span>
            <span className="text-muted-foreground hidden truncate text-xs sm:inline">
              {inv.employee_name}
            </span>
            <Badge variant={staffInvoiceStatusVariant(inv.status)} className="shrink-0">
              {staffInvoiceStatusLabel(inv.status)}
            </Badge>
            <span className="text-xs tabular-nums">{money(inv)}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function StaffInvoicesGroupedByStatus({ rows, onOpen }: ListProps) {
  const groups = useMemo(
    () =>
      STAFF_INVOICE_STATUSES.map((s) => ({
        key: s.value,
        label: s.label,
        bucket: rows.filter((inv) => inv.status === s.value),
      })),
    [rows],
  );

  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => {
        if (bucket.length === 0) return null;
        return (
          <section key={key} aria-labelledby={`staff-invoice-status-${key}`}>
            <h2
              id={`staff-invoice-status-${key}`}
              className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase"
            >
              {label}
              <Badge variant="outline" className="text-[10px]">
                {bucket.length}
              </Badge>
            </h2>
            <StaffInvoicesCards rows={bucket} onOpen={onOpen} />
          </section>
        );
      })}
    </div>
  );
}

function StaffInvoicesSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}
